import { TotalVolumeCapOperator } from 'src/modules/conversions/domain';

// The UI takes a percentage (0..100); the server evaluates `utilization` as a ratio (0..1).
export interface UtilizationThresholdFormValues {
  utilizationThresholdEnabled: boolean;
  utilizationOperator: TotalVolumeCapOperator | null;
  utilizationPercentage: number | null;
}

export const INITIAL_UTILIZATION_THRESHOLD_FORM_VALUES: UtilizationThresholdFormValues = {
  utilizationThresholdEnabled: false,
  utilizationOperator: null,
  utilizationPercentage: null,
};

export type ParsedUtilizationCondition =
  | { kind: 'none' }
  | { kind: 'threshold'; operator: TotalVolumeCapOperator; percentage: number }
  | { kind: 'unrepresentable'; raw: string };

// The variable is case-sensitive on the server. The leading `:` is optional there; the trigger
// mapper strips it on read, but accept it anyway so the parser does not depend on that.
const UTILIZATION_CONDITION_REGEX = /^\s*:?utilization\s*(>=|>|<=|<)\s*(\d+(?:\.\d+)?)\s*$/;

// Rounding keeps float noise out of the expression (33.3 / 100 is 0.33299999999999996).
const toRatio = (percentage: number) => Number((percentage / 100).toFixed(6));
const toPercentage = (ratio: number) => Number((ratio * 100).toFixed(4));

export const encodeUtilizationCondition = (operator: TotalVolumeCapOperator, percentage: number): string =>
  `utilization ${operator} ${toRatio(Number(percentage))}`;

export const parseUtilizationCondition = (expression: string | null | undefined): ParsedUtilizationCondition => {
  if (!expression || !expression.trim()) return { kind: 'none' };

  const match = UTILIZATION_CONDITION_REGEX.exec(expression);
  if (!match) return { kind: 'unrepresentable', raw: expression };

  const ratio = Number(match[2]);
  if (ratio < 0 || ratio > 1) return { kind: 'unrepresentable', raw: expression };

  return { kind: 'threshold', operator: match[1] as TotalVolumeCapOperator, percentage: toPercentage(ratio) };
};

export const buildUtilizationCondition = (values: UtilizationThresholdFormValues): string | undefined => {
  const { utilizationThresholdEnabled, utilizationOperator, utilizationPercentage } = values;
  if (!utilizationThresholdEnabled || !utilizationOperator || utilizationPercentage == null) return undefined;

  return encodeUtilizationCondition(utilizationOperator, utilizationPercentage);
};

// The server rejects "" — removing a condition takes null, and omitting the field leaves it as is.
export const buildUtilizationConditionUpdate = (
  values: Partial<UtilizationThresholdFormValues>,
  original: ParsedUtilizationCondition,
): string | null | undefined => {
  if (original.kind === 'unrepresentable') return undefined;

  const next = buildUtilizationCondition({ ...INITIAL_UTILIZATION_THRESHOLD_FORM_VALUES, ...values });
  const previous =
    original.kind === 'threshold' ? encodeUtilizationCondition(original.operator, original.percentage) : undefined;

  if (next === previous) return undefined;
  if (next === undefined) return null;
  return next;
};

// Not a table built at load time: conversions/domain imports from triggers/domain, so if that import
// ever becomes a runtime cycle the enum can still be undefined while this module is evaluated.
const operatorGlyph = (operator: TotalVolumeCapOperator) => {
  if (operator === TotalVolumeCapOperator.GREATER_OR_EQUAL) return '≥';
  if (operator === TotalVolumeCapOperator.LOWER_OR_EQUAL) return '≤';
  return operator;
};

export const formatUtilizationCondition = (expression: string | null | undefined): string => {
  const parsed = parseUtilizationCondition(expression);
  if (parsed.kind === 'none') return '-';
  if (parsed.kind === 'unrepresentable') return parsed.raw;
  return `Utilization ${operatorGlyph(parsed.operator)} ${parsed.percentage}%`;
};
